import React from 'react';
import { styled } from '@linaria/react';
import { Select, Button, Label } from '@livecommerce/design-system';
import { useUser } from 'contexts/UserContext.jsx';
import { useLocalization, getSupportedBrowserLocales, SUPPORTED_LOCALES } from 'contexts/LocalizationContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { getLanguageDisplayName, getLanguageCodeFromLocale } from 'helpers/localization.js';
import { LanguageSelectorBrowserLanguage, LanguageSelectorSelectLanguage } from 'services/tracking/dataClickIdEventNames.js';

const capitalizeFirstChar = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Container = styled.div`
  padding-top: 24px;
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const submitTrackingData = ({ language, locale }) => ({
  eventType: 'user-language:submit',
  payload: {
    language,
    locale,
  },
});

const languageOptions = Object.keys(SUPPORTED_LOCALES).reduce((accumulatedOptions, localeKey) => {
  const option = SUPPORTED_LOCALES[localeKey];
  if (!option.available) return accumulatedOptions;
  return [...accumulatedOptions, {
    value: option.locale,
    label: capitalizeFirstChar(getLanguageDisplayName(option.locale)),
  }];
}, []);

const UserLanguage = () => {
  const { t } = useLocalization();
  const { updateUserData, loading, refreshUserData, isUpdating } = useUser();
  const { locale } = useLocalization();
  const supportedBrowserLanguages = getSupportedBrowserLocales();

  const onSubmit = async ({ selectedItem, clickId } = {}) => {
    const clickIdEventFunction = clickId ? clickId : LanguageSelectorSelectLanguage;
    trackingService.trackClick({ clickId: clickIdEventFunction(selectedItem.value) });
    trackingService.track(submitTrackingData({ language: selectedItem.label, locale: selectedItem.value }));

    await updateUserData(
      {
        locale: selectedItem.value,
      },
      {
        catchCallback: () => {
          trackingService.track({
            ...submitTrackingData({ language: selectedItem.label, locale: selectedItem.value }),
            actionSuccess: false,
          });
        },
        finallyCallback: (_, error) => {
          if (error) return;
          refreshUserData();
        },
      },
    );
    trackingService.track({
      ...submitTrackingData({ language: selectedItem.label, locale: selectedItem.value }),
      actionSuccess: true,
    });
  };

  return (
    <Container>
      <Select
        id="user-language"
        label={t('profile.languageLabel')}
        items={languageOptions}
        selectedItem={languageOptions.find(option => getLanguageCodeFromLocale(option.value) === getLanguageCodeFromLocale(locale))}
        onSelectedItemChange={onSubmit}
        disabled={loading || isUpdating}
      />
      {supportedBrowserLanguages.length && (
        <>
          <Label>
            {t('profile.supportedBrowserLanguagesLabel')}
          </Label>
          <Buttons>
            {supportedBrowserLanguages.map((language) => {
              const matchingOption = languageOptions.find(option => option.value === language);
              return (
                <Button
                  disabled={loading || isUpdating}
                  selected={getLanguageCodeFromLocale(locale) === getLanguageCodeFromLocale(language)}
                  key={language}
                  variant="solidGrey"
                  size="small"
                  isUppercase={false}
                  onClick={() => {
                    onSubmit({ selectedItem: matchingOption, clickId: LanguageSelectorBrowserLanguage });
                  }}
                >
                  {matchingOption.label}
                </Button>
              );
            })}
          </Buttons>
        </>
      )}
    </Container>
  );
};

export default UserLanguage;
